import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  CopyButton,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconBrandGoogle,
  IconCheck,
  IconCopy,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconClock,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { SecretField } from '../../components/config/SecretField';
import { PermissionGate } from '../../components/PermissionGate';
import { ConflictResolutionDashboard } from '../../components/google/ConflictResolutionDashboard';
import { SpecialHoursForm, type SpecialHourPeriod } from '../../components/google/SpecialHoursForm';

function GoogleByocCard({
  byocClientId,
  hasByocClientSecret,
}: {
  byocClientId: string | null;
  hasByocClientSecret: boolean;
}) {
  const utils = trpc.useUtils();
  const [clientId, setClientId] = useState(byocClientId || '');
  const [clientSecret, setClientSecret] = useState('');

  useEffect(() => {
    setClientId(byocClientId || '');
    setClientSecret('');
  }, [byocClientId, hasByocClientSecret]);

  const setMany = trpc.tenancy.settings.setMany.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Google App credentials saved', color: 'green' });
      void utils.integrations.google.status.invalidate();
      setClientSecret('');
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const onSave = () => {
    const settings: Record<string, string | null> = {
      google_client_id_byoc: clientId.trim() || null,
    };
    if (clientSecret.length > 0) {
      settings['google_client_secret_byoc'] = clientSecret;
    }
    setMany.mutate({ settings });
  };

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="sm">
        <Group gap="xs">
          <IconBrandGoogle size={20} color="#ea4335" />
          <Title order={4}>Custom Google App Credentials (BYOC)</Title>
          <Badge variant="light" color={hasByocClientSecret ? 'green' : 'red'}>
            {hasByocClientSecret ? 'secret saved' : 'secret missing'}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Optional: Provide your own Google Cloud Console credentials to run the OAuth flow under
          your own brand. Make sure to add{' '}
          <code>{window.location.origin}/api/google/oauth/callback</code> to your Google App's
          Authorized redirect URIs.
        </Text>

        <TextInput
          label="Google Client ID"
          placeholder="Enter your Google Client ID"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
        />

        <SecretField
          label="Google Client Secret"
          placeholder="Enter your Google Client Secret"
          present={hasByocClientSecret}
          value={clientSecret}
          onChange={setClientSecret}
          onReveal={() => utils.integrations.google.revealClientSecret.fetch().then((r) => r.value)}
        />

        <Group justify="flex-end" mt="xs">
          <Button variant="filled" color="red" onClick={onSave} loading={setMany.isPending}>
            Save Credentials
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

const mapGoogleSpecialHours = (rawJson: string | null): SpecialHourPeriod[] => {
  if (!rawJson) return [];
  try {
    const parsed = JSON.parse(rawJson) as {
      specialHoursPeriods?: Array<{
        startDate: { year: number; month: number; day: number };
        openTime?: { hours: number; minutes: number };
        closeTime?: { hours: number; minutes: number };
        closed?: boolean;
      }>;
    };
    if (!parsed?.specialHoursPeriods) return [];
    return parsed.specialHoursPeriods.map((p) => {
      const date = new Date(p.startDate.year, p.startDate.month - 1, p.startDate.day);
      const openTime = p.openTime
        ? `${String(p.openTime.hours).padStart(2, '0')}:${String(p.openTime.minutes).padStart(2, '0')}`
        : '09:00';
      const closeTime = p.closeTime
        ? `${String(p.closeTime.hours).padStart(2, '0')}:${String(p.closeTime.minutes).padStart(2, '0')}`
        : '17:00';
      return {
        date,
        openTime,
        closeTime,
        closed: !!p.closed,
      };
    });
  } catch {
    return [];
  }
};

const mapToGoogleSpecialHours = (periods: SpecialHourPeriod[]) => {
  return {
    specialHoursPeriods: periods.map((p) => {
      const year = p.date.getFullYear();
      const month = p.date.getMonth() + 1;
      const day = p.date.getDate();

      const item: {
        startDate: { year: number; month: number; day: number };
        closed: boolean;
        openTime?: { hours: number; minutes: number };
        closeTime?: { hours: number; minutes: number };
      } = {
        startDate: { year, month, day },
        closed: p.closed,
      };

      if (!p.closed) {
        const [openH, openM] = p.openTime.split(':').map(Number);
        const [closeH, closeM] = p.closeTime.split(':').map(Number);
        item.openTime = { hours: openH ?? 9, minutes: openM ?? 0 };
        item.closeTime = { hours: closeH ?? 17, minutes: closeM ?? 0 };
      }

      return item;
    }),
  };
};

function ConnectGoogle() {
  const status = trpc.integrations.google.status.useQuery();
  const utils = trpc.useUtils();
  const disconnectMutation = trpc.integrations.google.disconnect.useMutation();
  const setCustomerIdMutation = trpc.integrations.google.setCustomerId.useMutation();
  const generateKeyMutation = trpc.integrations.google.generateWebhookKey.useMutation();
  const syncLocationsMutation = trpc.integrations.google.syncLocations.useMutation();
  const syncNowMutation = trpc.integrations.google.syncNow.useMutation();
  const saveConversionSettingsMutation =
    trpc.integrations.google.saveConversionSettings.useMutation();
  const saveMerchantSettingsMutation = trpc.integrations.google.saveMerchantSettings.useMutation();
  const syncProductsMutation = trpc.integrations.google.syncProducts.useMutation();

  const [customerId, setCustomerId] = useState('');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [isSyncingLocations, setIsSyncingLocations] = useState(false);
  const [leadActionId, setLeadActionId] = useState('');
  const [purchaseActionId, setPurchaseActionId] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);

  const [hoursModalOpen, setHoursModalOpen] = useState(false);
  const [selectedLocForHours, setSelectedLocForHours] = useState<{
    locationName: string;
    displayName: string;
    specialHoursJson: string | null;
  } | null>(null);
  const [specialHours, setSpecialHours] = useState<SpecialHourPeriod[]>([]);
  const updateHoursMutation = trpc.integrations.google.updateLocationHours.useMutation();

  // Sync state from query
  useEffect(() => {
    if (status.data) {
      setCustomerId(status.data.customerId || '');
      setSelectedLocations(status.data.locations.map((l) => l.locationName));
      setLeadActionId(status.data.leadActionId || '');
      setPurchaseActionId(status.data.purchaseActionId || '');
      setMerchantId(status.data.merchantId || '');

      const firstLoc = status.data.locations[0];
      if (firstLoc && !activeLocationId) {
        setActiveLocationId(String(firstLoc.id));
      }
    }
  }, [status.data]);

  const handleManageHours = (loc: {
    locationName: string;
    displayName: string;
    specialHoursJson: string | null;
  }) => {
    setSelectedLocForHours(loc);
    setSpecialHours(mapGoogleSpecialHours(loc.specialHoursJson));
    setHoursModalOpen(true);
  };

  const handleSaveHours = async () => {
    if (!selectedLocForHours) return;
    try {
      await updateHoursMutation.mutateAsync({
        locationName: selectedLocForHours.locationName,
        specialHours: mapToGoogleSpecialHours(specialHours),
      });
      notifications.show({
        message: 'Holiday & Special hours successfully synchronized to Google.',
        color: 'green',
      });
      setHoursModalOpen(false);
      void status.refetch();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      notifications.show({
        message: `Failed to update hours: ${msg}`,
        color: 'red',
      });
    }
  };

  const handleConnect = () => {
    // Redirect to backend OAuth route
    window.location.href = '/api/google/oauth/login';
  };

  const handleDisconnect = async () => {
    if (
      confirm(
        'Are you sure you want to disconnect your Google account? This will remove all synced reviews.',
      )
    ) {
      await disconnectMutation.mutateAsync();
      notifications.show({
        message: 'Google account disconnected successfully',
        color: 'green',
      });
      void utils.integrations.google.status.invalidate();
    }
  };

  const handleSaveCustomerId = async () => {
    await setCustomerIdMutation.mutateAsync({ customerId });
    notifications.show({
      message: 'Google Ads Customer ID saved successfully',
      color: 'green',
    });
    void utils.integrations.google.status.invalidate();
  };

  const handleSaveConversionSettings = async () => {
    await saveConversionSettingsMutation.mutateAsync({
      leadActionId: leadActionId || null,
      purchaseActionId: purchaseActionId || null,
    });
    notifications.show({
      message: 'Google Ads Offline Conversion settings saved successfully',
      color: 'green',
    });
    void utils.integrations.google.status.invalidate();
  };

  const handleSaveMerchantSettings = async () => {
    await saveMerchantSettingsMutation.mutateAsync({
      merchantId: merchantId || null,
    });
    notifications.show({
      message: 'Google Merchant Center ID saved successfully',
      color: 'green',
    });
    void utils.integrations.google.status.invalidate();
  };

  const handleSyncProducts = async () => {
    try {
      const res = await syncProductsMutation.mutateAsync();
      notifications.show({
        message: `Successfully synced ${res.syncedCount} products to Google Merchant Center`,
        color: 'green',
      });
    } catch (err: any) {
      notifications.show({
        message: err.message || 'Failed to sync products',
        color: 'red',
      });
    }
  };

  const handleGenerateWebhookKey = async () => {
    await generateKeyMutation.mutateAsync();
    notifications.show({
      message: 'New webhook key generated successfully',
      color: 'green',
    });
    void utils.integrations.google.status.invalidate();
  };

  const handleLocationToggle = (locationName: string) => {
    setSelectedLocations((prev) =>
      prev.includes(locationName)
        ? prev.filter((name) => name !== locationName)
        : [...prev, locationName],
    );
  };

  const handleSaveLocations = async () => {
    setIsSyncingLocations(true);
    try {
      await syncLocationsMutation.mutateAsync({ locationNames: selectedLocations });
      notifications.show({
        message: 'Google Business Profile locations synced successfully',
        color: 'green',
      });
      void utils.integrations.google.status.invalidate();
    } catch (err) {
      notifications.show({
        message: 'Failed to sync locations',
        color: 'red',
      });
    } finally {
      setIsSyncingLocations(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      await syncNowMutation.mutateAsync();
      notifications.show({
        message: 'Manual sync triggered successfully',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        message: 'Sync failed',
        color: 'red',
      });
    }
  };

  const { data: availableLocations = [], isLoading: isLoadingLocations } =
    trpc.integrations.google.listLocations.useQuery(undefined, {
      enabled: !!status.data?.connected,
    });

  // Group locations by Google Business Account (Profile)
  const locationsByAccount = availableLocations.reduce<
    Record<
      string,
      {
        accountName: string;
        accountDisplayName: string;
        locations: typeof availableLocations;
      }
    >
  >((acc, loc) => {
    const key = loc.accountDisplayName || loc.accountName || 'Default Account';
    if (!acc[key]) {
      acc[key] = {
        accountName: loc.accountName || '',
        accountDisplayName: key,
        locations: [],
      };
    }
    acc[key].locations.push(loc);
    return acc;
  }, {});

  const handleAccountToggle = (locationsInAccount: typeof availableLocations, checked: boolean) => {
    const namesInAccount = locationsInAccount.map((l) => l.locationName);
    if (checked) {
      setSelectedLocations((prev) => [
        ...prev,
        ...namesInAccount.filter((name) => !prev.includes(name)),
      ]);
    } else {
      setSelectedLocations((prev) => prev.filter((name) => !namesInAccount.includes(name)));
    }
  };

  const apiPublicUrl = window.location.origin;
  const webhookUrl = status.data?.webhookKey
    ? `${apiPublicUrl}/api/public/google/leads?google_key=${status.data.webhookKey}`
    : 'Generate a webhook key first';

  if (status.isLoading) {
    return (
      <Stack align="center" justify="center" py={40}>
        <IconRefresh size={32} className="animate-spin" />
        <Text size="sm" c="dimmed">
          Loading Google integration status...
        </Text>
      </Stack>
    );
  }

  const activeLocation = status.data?.locations.find((l) => String(l.id) === activeLocationId);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Stack gap={2}>
            <Title order={2}>Google Integration</Title>
            <Text c="dimmed" size="sm">
              Connect your Google account to sync Google Ads leads, Local Services Ads, and manage
              Google Business Profile reviews.
            </Text>
          </Stack>
          <Group gap="md">
            {status.data?.connected && status.data.locations.length > 0 && (
              <Select
                placeholder="Select active profile"
                data={status.data.locations.map((l) => ({
                  value: String(l.id),
                  label: l.displayName,
                }))}
                value={activeLocationId}
                onChange={(val) => setActiveLocationId(val)}
                style={{ width: 250 }}
              />
            )}
            <Badge variant="light" leftSection={<IconBrandGoogle size={12} />} color="red">
              Google Business
            </Badge>
          </Group>
        </Group>

        <Tabs defaultValue="settings">
          <Tabs.List>
            <Tabs.Tab value="settings">Settings & Locations</Tabs.Tab>
            <Tabs.Tab value="posts" disabled={!status.data?.connected}>
              Local Posts
            </Tabs.Tab>
            <Tabs.Tab value="qa" disabled={!status.data?.connected}>
              Questions & Answers
            </Tabs.Tab>
            <Tabs.Tab value="conflicts" disabled={!status.data?.connected}>
              Override Conflicts
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="settings" pt="md">
            <Stack gap="lg">
              {/* 1. Connection Card */}
              <Card withBorder padding="lg" radius="md">
                <Group justify="space-between" align="center">
                  <Group gap="md">
                    <IconBrandGoogle size={36} color="#ea4335" />
                    <Stack gap={2}>
                      <Text fw={600} size="md">
                        {status.data?.connected
                          ? `Connected as ${status.data.email}`
                          : 'Google Account'}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {status.data?.connected
                          ? `Connected on {new Date(status.data.connectedAt!).toLocaleDateString()}`
                          : 'Authorize LeadSync to access Google Business Profile and Google Ads'}
                      </Text>
                    </Stack>
                  </Group>

                  {status.data?.connected ? (
                    <Group gap="sm">
                      <Button
                        variant="light"
                        color="gray"
                        leftSection={<IconRefresh size={16} />}
                        onClick={handleSyncNow}
                        loading={syncNowMutation.isPending}
                      >
                        Sync Now
                      </Button>
                      <Button
                        variant="light"
                        color="red"
                        leftSection={<IconTrash size={16} />}
                        onClick={handleDisconnect}
                        loading={disconnectMutation.isPending}
                      >
                        Disconnect
                      </Button>
                    </Group>
                  ) : (
                    <Button
                      color="red"
                      leftSection={<IconBrandGoogle size={16} />}
                      onClick={handleConnect}
                    >
                      Connect Account
                    </Button>
                  )}
                </Group>
              </Card>

              {/* BYOC Card */}
              <GoogleByocCard
                byocClientId={status.data?.byocClientId ?? null}
                hasByocClientSecret={!!status.data?.hasByocClientSecret}
              />

              {status.data?.connected && (
                <>
                  {/* 2. Google Business Profile - Locations Sync */}
                  <Card withBorder padding="lg" radius="md">
                    <Stack gap="md">
                      <Title order={4}>Google Business Profile Locations</Title>
                      <Text size="sm" c="dimmed">
                        Select which business locations you want to manage reviews for. We will
                        fetch and sync reviews for these locations.
                      </Text>

                      {isLoadingLocations ? (
                        <Group gap="xs">
                          <IconRefresh size={16} className="animate-spin" />
                          <Text size="xs" c="dimmed">
                            Fetching locations...
                          </Text>
                        </Group>
                      ) : availableLocations.length === 0 ? (
                        <Alert
                          icon={<IconAlertCircle size={16} />}
                          title="No locations found"
                          color="blue"
                        >
                          No business locations were found in your Google Business Profile. Please
                          ensure your account has verified locations.
                        </Alert>
                      ) : (
                        <Stack gap="md">
                          {Object.values(locationsByAccount).map((group) => {
                            const allChecked = group.locations.every((l) =>
                              selectedLocations.includes(l.locationName),
                            );
                            const someChecked = group.locations.some((l) =>
                              selectedLocations.includes(l.locationName),
                            );
                            const isIndeterminate = someChecked && !allChecked;

                            return (
                              <Card
                                key={group.accountDisplayName}
                                withBorder
                                padding="md"
                                radius="sm"
                              >
                                <Stack gap="sm">
                                  <Group justify="space-between">
                                    <Group gap="sm">
                                      <Checkbox
                                        checked={allChecked}
                                        indeterminate={isIndeterminate}
                                        onChange={(e) =>
                                          handleAccountToggle(
                                            group.locations,
                                            e.currentTarget.checked,
                                          )
                                        }
                                      />
                                      <Text fw={600} size="sm">
                                        {group.accountDisplayName}
                                      </Text>
                                    </Group>
                                    <Badge size="xs" color="blue" variant="light">
                                      {group.locations.length}{' '}
                                      {group.locations.length === 1 ? 'location' : 'locations'}
                                    </Badge>
                                  </Group>
                                  <Divider />
                                  <Stack gap="xs" pl="lg">
                                    {group.locations.map((loc) => {
                                      const isChecked = selectedLocations.includes(
                                        loc.locationName,
                                      );
                                      return (
                                        <Card
                                          key={loc.locationName}
                                          withBorder
                                          padding="xs"
                                          radius="sm"
                                        >
                                          <Group justify="space-between">
                                            <Group gap="sm">
                                              <Checkbox
                                                checked={isChecked}
                                                onChange={() =>
                                                  handleLocationToggle(loc.locationName)
                                                }
                                              />
                                              <Stack gap={0}>
                                                <Text fw={500} size="sm">
                                                  {loc.displayName}
                                                </Text>
                                                <Text size="xs" c="dimmed">
                                                  {loc.primaryCategory} •{' '}
                                                  {loc.address || 'No address listed'}
                                                </Text>
                                              </Stack>
                                            </Group>
                                            {isChecked && (
                                              <Group gap="xs">
                                                <Button
                                                  size="xs"
                                                  variant="subtle"
                                                  color="red"
                                                  leftSection={<IconClock size={12} />}
                                                  onClick={() => {
                                                    const syncedLoc = status.data?.locations.find(
                                                      (l) => l.locationName === loc.locationName,
                                                    );
                                                    handleManageHours({
                                                      locationName: loc.locationName,
                                                      displayName: loc.displayName,
                                                      specialHoursJson:
                                                        syncedLoc?.specialHoursJson ?? null,
                                                    });
                                                  }}
                                                >
                                                  Hours
                                                </Button>
                                                <Badge size="xs" color="green" variant="light">
                                                  Synced
                                                </Badge>
                                              </Group>
                                            )}
                                          </Group>
                                        </Card>
                                      );
                                    })}
                                  </Stack>
                                </Stack>
                              </Card>
                            );
                          })}

                          <Group justify="flex-end" mt="sm">
                            <Button
                              variant="filled"
                              color="red"
                              onClick={handleSaveLocations}
                              loading={isSyncingLocations}
                            >
                              Save Locations
                            </Button>
                          </Group>
                        </Stack>
                      )}
                    </Stack>
                  </Card>

                  {/* 3. Google Ads Lead Form Webhook */}
                  <Card withBorder padding="lg" radius="md">
                    <Stack gap="md">
                      <Title order={4}>Google Ads Lead Form Webhook</Title>
                      <Text size="sm" c="dimmed">
                        Use this webhook URL in your Google Ads Lead Form extensions to receive
                        leads instantly in your LeadSync CRM.
                      </Text>

                      <Group align="flex-end" gap="xs">
                        <TextInput
                          label="Webhook URL"
                          value={webhookUrl}
                          readOnly
                          style={{ flex: 1 }}
                          description="Paste this into the Webhook URL field in your Google Ads Lead Form settings."
                        />
                        <CopyButton value={webhookUrl}>
                          {({ copied, copy }) => (
                            <Tooltip
                              label={copied ? 'Copied' : 'Copy URL'}
                              withArrow
                              position="top"
                            >
                              <ActionIcon
                                color={copied ? 'teal' : 'gray'}
                                variant="subtle"
                                onClick={copy}
                                size="lg"
                                mb={2}
                              >
                                {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Group>

                      <Group justify="space-between" align="center" mt="xs">
                        <Stack gap={2}>
                          <Text size="xs" fw={500}>
                            Webhook Security Key
                          </Text>
                          <Text size="xs" c="dimmed">
                            {status.data?.webhookKey
                              ? `Key: ${status.data.webhookKey}`
                              : 'No key generated yet'}
                          </Text>
                        </Stack>
                        <Button
                          variant="outline"
                          color="red"
                          size="xs"
                          onClick={handleGenerateWebhookKey}
                          loading={generateKeyMutation.isPending}
                        >
                          {status.data?.webhookKey ? 'Regenerate Key' : 'Generate Key'}
                        </Button>
                      </Group>
                    </Stack>
                  </Card>

                  {/* 4. Local Services Ads Integration */}
                  <Card withBorder padding="lg" radius="md">
                    <Stack gap="md">
                      <Title order={4}>Google Local Services Ads (LSA)</Title>
                      <Text size="sm" c="dimmed">
                        Enter your Google Ads Customer ID to enable automatic syncing of leads from
                        Google Local Services Ads.
                      </Text>

                      <Group align="flex-end" gap="xs">
                        <TextInput
                          label="Google Ads Customer ID"
                          placeholder="e.g. 123-456-7890"
                          value={customerId}
                          onChange={(e) => setCustomerId(e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <Button
                          color="red"
                          onClick={handleSaveCustomerId}
                          loading={setCustomerIdMutation.isPending}
                        >
                          Save ID
                        </Button>
                      </Group>
                    </Stack>
                  </Card>

                  {/* 5. Google Ads Offline Conversion Tracking */}
                  <Card withBorder padding="lg" radius="md">
                    <Stack gap="md">
                      <Title order={4}>Google Ads Offline Conversion Tracking (OCT)</Title>
                      <Text size="sm" c="dimmed">
                        Configure your Google Ads Conversion Action IDs. LeadSync will automatically
                        upload offline conversions (leads or purchases) back to Google Ads for
                        customers who clicked your ads.
                      </Text>

                      <Stack gap="sm">
                        <TextInput
                          label="Google Ads Conversion Action ID (Lead)"
                          placeholder="e.g. 12345678"
                          value={leadActionId}
                          onChange={(e) => setLeadActionId(e.target.value)}
                          description="Triggered when a lead is captured or marked as lead."
                        />
                        <TextInput
                          label="Google Ads Conversion Action ID (Purchase)"
                          placeholder="e.g. 87654321"
                          value={purchaseActionId}
                          onChange={(e) => setPurchaseActionId(e.target.value)}
                          description="Triggered when the Closed Won or Converted label is added to a contact."
                        />
                        <Group justify="flex-end" mt="xs">
                          <Button
                            color="red"
                            onClick={handleSaveConversionSettings}
                            loading={saveConversionSettingsMutation.isPending}
                          >
                            Save Conversion Settings
                          </Button>
                        </Group>
                      </Stack>
                    </Stack>
                  </Card>

                  {/* 6. Google Merchant Center Product Sync */}
                  <Card withBorder padding="lg" radius="md">
                    <Stack gap="md">
                      <Title order={4}>Google Merchant Center Product Sync</Title>
                      <Text size="sm" c="dimmed">
                        Connect your Google Merchant Center account to automatically sync your local
                        e-commerce product catalog (Shopify or WooCommerce) so your products appear
                        in Google Shopping and Google Maps.
                      </Text>

                      <Stack gap="sm">
                        <TextInput
                          label="Google Merchant Center ID"
                          placeholder="e.g. 123456789"
                          value={merchantId}
                          onChange={(e) => setMerchantId(e.target.value)}
                          description="Your Google Merchant Center account identifier."
                        />
                        <Group justify="space-between" mt="xs">
                          <Button
                            variant="light"
                            color="red"
                            onClick={handleSyncProducts}
                            loading={syncProductsMutation.isPending}
                            disabled={!status.data?.connected || !merchantId}
                          >
                            Sync Products Now
                          </Button>
                          <Button
                            color="red"
                            onClick={handleSaveMerchantSettings}
                            loading={saveMerchantSettingsMutation.isPending}
                          >
                            Save Merchant ID
                          </Button>
                        </Group>
                      </Stack>
                    </Stack>
                  </Card>
                </>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="posts" pt="md">
            <GooglePostsTab activeLocation={activeLocation} />
          </Tabs.Panel>

          <Tabs.Panel value="qa" pt="md">
            <GoogleQaTab
              activeLocationId={activeLocationId ? Number(activeLocationId) : undefined}
            />
          </Tabs.Panel>

          <Tabs.Panel value="conflicts" pt="md">
            <ConflictResolutionDashboard />
          </Tabs.Panel>
        </Tabs>

        <Modal
          opened={hoursModalOpen}
          onClose={() => setHoursModalOpen(false)}
          title={`Configure Hours - ${selectedLocForHours?.displayName || ''}`}
          size="md"
        >
          <Stack gap="md">
            <SpecialHoursForm value={specialHours} onChange={setSpecialHours} />
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => setHoursModalOpen(false)}>
                Cancel
              </Button>
              <Button color="red" onClick={handleSaveHours} loading={updateHoursMutation.isPending}>
                Save Exceptions
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </motion.div>
  );
}

function GooglePostsTab({
  activeLocation,
}: {
  activeLocation?: { id: number; locationName: string; displayName: string };
}) {
  const [postOpen, setPostOpen] = useState(false);
  const [summary, setSummary] = useState('');
  const [ctaType, setCtaType] = useState<string>('');
  const [ctaUrl, setCtaUrl] = useState('');

  const createPostMutation = trpc.integrations.google.createPost.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Google Post published successfully', color: 'green' });
      setPostOpen(false);
      setSummary('');
      setCtaType('');
      setCtaUrl('');
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const handlePublish = async () => {
    if (!activeLocation) {
      notifications.show({ message: 'No active profile selected', color: 'red' });
      return;
    }
    if (!summary) {
      notifications.show({ message: 'Please enter a summary', color: 'red' });
      return;
    }
    await createPostMutation.mutateAsync({
      locationName: activeLocation.locationName,
      summary,
      ctaType: ctaType ? (ctaType as any) : undefined,
      ctaUrl: ctaUrl || undefined,
    });
  };

  if (!activeLocation) {
    return (
      <Card withBorder padding="lg" radius="md">
        <Text c="dimmed" size="sm">
          Please select an active profile from the switcher above to view and manage posts.
        </Text>
      </Card>
    );
  }

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Stack gap={2}>
            <Title order={4}>Google Local Posts — {activeLocation.displayName}</Title>
            <Text size="sm" c="dimmed">
              Create and manage posts that appear on Google Search and Maps for this location.
            </Text>
          </Stack>
          <Button
            color="red"
            leftSection={<IconPlus size={16} />}
            onClick={() => setPostOpen(true)}
          >
            Create Post
          </Button>
        </Group>

        <Divider />

        <Alert title="Simulation Info" color="blue" icon={<IconAlertCircle size={16} />}>
          To publish posts to a live Google Business Profile, your locations must be verified by
          Google. Use the button above to create and test post publishing.
        </Alert>

        <Modal
          opened={postOpen}
          onClose={() => setPostOpen(false)}
          title="Create Google Local Post"
        >
          <Stack gap="md">
            <Text size="sm" fw={500}>
              Publishing to:{' '}
              <span style={{ color: 'var(--mantine-color-red-6)' }}>
                {activeLocation.displayName}
              </span>
            </Text>

            <Textarea
              label="Post Summary"
              placeholder="What's new with your business?"
              minRows={4}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />

            <Select
              label="Call to Action Button (Optional)"
              placeholder="None"
              data={[
                { value: '', label: 'None' },
                { value: 'BOOK', label: 'Book' },
                { value: 'ORDER', label: 'Order Online' },
                { value: 'LEARN_MORE', label: 'Learn More' },
                { value: 'SIGN_UP', label: 'Sign Up' },
                { value: 'CALL', label: 'Call Now' },
              ]}
              value={ctaType}
              onChange={(val) => setCtaType(val || '')}
            />

            {ctaType && ctaType !== 'CALL' && (
              <TextInput
                label="Button Link"
                placeholder="https://example.com"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
              />
            )}

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => setPostOpen(false)}>
                Cancel
              </Button>
              <Button color="red" onClick={handlePublish} loading={createPostMutation.isPending}>
                Publish Post
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Card>
  );
}

function GoogleQaTab({ activeLocationId }: { activeLocationId?: number }) {
  const {
    data: questions = [],
    isLoading,
    refetch,
  } = trpc.integrations.google.listQuestions.useQuery(
    { googleLocationId: activeLocationId },
    { enabled: activeLocationId !== undefined },
  );
  const [replyOpen, setReplyOpen] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState<any>(null);
  const [answerText, setAnswerText] = useState('');

  const answerQuestionMutation = trpc.integrations.google.answerQuestion.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Answer submitted successfully', color: 'green' });
      setReplyOpen(false);
      setAnswerText('');
      void refetch();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const handleSubmitAnswer = async () => {
    if (!answerText) {
      notifications.show({ message: 'Please type an answer', color: 'red' });
      return;
    }
    await answerQuestionMutation.mutateAsync({
      questionName: activeQuestion.name,
      answerText,
    });
  };

  if (isLoading) {
    return (
      <Group gap="xs" justify="center" py={40}>
        <IconRefresh size={16} className="animate-spin" />
        <Text size="xs" c="dimmed">
          Loading questions...
        </Text>
      </Group>
    );
  }

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Stack gap={2}>
            <Title order={4}>Google Questions & Answers</Title>
            <Text size="sm" c="dimmed">
              Answer customer questions asked on Google Search and Maps.
            </Text>
          </Stack>
          <Button
            variant="light"
            color="gray"
            leftSection={<IconRefresh size={16} />}
            onClick={() => void refetch()}
          >
            Refresh
          </Button>
        </Group>

        <Divider />

        {questions.length === 0 ? (
          <Alert title="No questions found" color="blue" icon={<IconAlertCircle size={16} />}>
            No customer questions have been asked for your connected locations yet.
          </Alert>
        ) : (
          <Stack gap="md">
            {questions.map((q: any) => (
              <Card key={q.name} withBorder padding="sm" radius="sm">
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Group gap="xs">
                      <Text fw={600} size="sm">
                        {q.authorName}
                      </Text>
                      <Text size="xs" c="dimmed">
                        on {q.locationDisplayName}
                      </Text>
                    </Group>
                    <Badge size="xs" color={q.answer ? 'green' : 'orange'} variant="light">
                      {q.answer ? 'Answered' : 'Unanswered'}
                    </Badge>
                  </Group>

                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {q.text}
                  </Text>

                  <Text size="xs" c="dimmed">
                    Asked on {new Date(q.createdAt).toLocaleString()}
                  </Text>

                  {q.answer ? (
                    <Card withBorder padding="xs" radius="xs" bg="gray.0" mt="xs">
                      <Stack gap={4}>
                        <Group justify="space-between">
                          <Text size="xs" fw={600}>
                            Owner Answer ({q.answer.authorName})
                          </Text>
                          <Button
                            variant="subtle"
                            color="red"
                            size="xs"
                            onClick={() => {
                              setActiveQuestion(q);
                              setAnswerText(q.answer.text);
                              setReplyOpen(true);
                            }}
                          >
                            Edit Answer
                          </Button>
                        </Group>
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                          {q.answer.text}
                        </Text>
                        <Text size="xs" c="dimmed">
                          Answered on {new Date(q.answer.createdAt).toLocaleString()}
                        </Text>
                      </Stack>
                    </Card>
                  ) : (
                    <Group justify="flex-end" mt="xs">
                      <Button
                        size="xs"
                        color="red"
                        onClick={() => {
                          setActiveQuestion(q);
                          setAnswerText('');
                          setReplyOpen(true);
                        }}
                      >
                        Answer Question
                      </Button>
                    </Group>
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
        )}

        <Modal
          opened={replyOpen}
          onClose={() => setReplyOpen(false)}
          title="Answer Google Question"
        >
          <Stack gap="md">
            {activeQuestion && (
              <>
                <Text size="sm" fw={500}>
                  Question from {activeQuestion.authorName}:
                </Text>
                <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                  "{activeQuestion.text}"
                </Text>
              </>
            )}

            <Textarea
              label="Your Answer"
              placeholder="Type your official response..."
              minRows={3}
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => setReplyOpen(false)}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleSubmitAnswer}
                loading={answerQuestionMutation.isPending}
              >
                Submit Answer
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Card>
  );
}

function ConnectGoogleGated() {
  return (
    <PermissionGate permission="manage_integrations">
      <ConnectGoogle />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/integrations/google')({
  component: ConnectGoogleGated,
});
